/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../../globalScope/GlobalScope';
import Disposable from '../../lang/Disposable';
import DisposableList from '../../lang/DisposableList';
import assertUnreachable from '../../lang/assertUnreachable';
import {StreamSetupListener} from '../../dom/StreamSetupListener';
import GarbageCollectorManager from '../../dom/GarbageCollectorManager';
import Durations from '../../time/Duration';
import SDK from '../SDK';
import IPeerConnection from '../../rtc/IPeerConnection';
import SdpParser from '../../rtc/SdpParser';
import RtcConfigurationManager from '../../rtc/RtcConfigurationManager';
import PeerConnectionService, {
  defaultTrackKinds,
  EncodedInsertableStreams
} from '../../rtc/PeerConnectionService';
import RtcConnectionMonitor from '../../rtc/RtcConnectionMonitor';
import EndPoint, {SubscribeOptions, SetRemoteDescriptionStatus} from '../discovery/EndPoint';
import Discovery from '../discovery/Discovery';
import FeatureEnablement from '../../environment/FeatureEnablement';
import ChannelState from '../channels/ChannelState';
import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import ChannelContext from '../context/ChannelContext';
import PeerConnectionContext from '../context/PeerConnectionContext';
import StreamTransformContext from '../context/StreamTransformContext';
import FrameTimingInformationContext from '../context/FrameTimingInformationContext';
import FrameTimingInformationManager, {FrameTimingInformationManagerContext} from '../frameTiming/FrameTimingInformationManager';
import InsertableStreams from '../transformation/InsertableStreams';
import {IStreamSubscriber} from './IStreamSubscriber';
import VideoFrameDetector from './VideoFrameDetector';
import EncodedMediaTransformManager, {EncodedMediaTransformManagerContext} from '../transformation/EncodedMediaTransformManager';
import {AudioCodecType} from '../decode/bitstream/codec/AudioCodec';
import {VideoCodecType} from '../decode/bitstream/codec/VideoCodec';
import VideoCodecMapping from '../decode/bitstream/codec/VideoCodecMapping';
import AudioCodecMapping from '../decode/bitstream/codec/AudioCodecMapping';

const iceCandidateAccumulationInterval = 100;
const defaultStreamSetupTimeout = 30000;

export default class RealTimeStreamSubscriber implements IStreamSubscriber {
  private readonly _logger: ILogger = LoggerFactory.getLogger('RealTimeStreamSubscriber');
  private readonly _disposables: DisposableList = new DisposableList();
  private readonly _channelContext: ChannelContext;
  private readonly _peerConnectionContext: PeerConnectionContext;
  private readonly _streamTransformContext: StreamTransformContext;
  private readonly _frameTimingInformationContext: FrameTimingInformationContext;
  private readonly _handleStreamFailure: () => Promise<void>;

  constructor(channelContext: ChannelContext, peerConnectionContext: PeerConnectionContext, streamTransformContext: StreamTransformContext, frameTimingInformationContext: FrameTimingInformationContext, handleStreamFailure: () => Promise<void>) {
    this._channelContext = channelContext;
    this._peerConnectionContext = peerConnectionContext;
    this._streamTransformContext = streamTransformContext;
    this._frameTimingInformationContext = frameTimingInformationContext;
    this._handleStreamFailure = handleStreamFailure;
  }

  start(uri: URL, token: string, listenOnStreamSetup: StreamSetupListener, playMediaStreamInVideoElement: Function): Promise<void> {
    this._disposables.dispose();

    const requiresEncodedInsertableStreams = this._streamTransformContext.hasEncodedInsertableStreams.value
      || this._frameTimingInformationContext.shouldExtractTimecode.value
      || this._frameTimingInformationContext.shouldExtractTimestamps.value ? EncodedInsertableStreams.Enabled : EncodedInsertableStreams.Disabled;

    return Promise.all([
      Discovery.discoverClosestEndPointWithCaching(uri),
      PeerConnectionService.createPeerConnectionOffer('recvonly', defaultTrackKinds, requiresEncodedInsertableStreams)
        .then(({localOffer, peerConnection}) => {
          this._peerConnectionContext.peerConnection.value = peerConnection;

          return {
            localOffer,
            peerConnection
          };
        })
    ])
      .then(([endPoint, {localOffer, peerConnection}]) => {
        this._channelContext.online.value = true;
        this._channelContext.endPoint.value = endPoint;
        this._logger.info('Connecting to [%s]', endPoint.toString());
        this._logger.info('Local offer:\n' + localOffer.sdp);

        const subscribeOptions: SubscribeOptions = {};

        if (this._channelContext.onRealTimeSubscribeRequestCallback) {
          subscribeOptions.onSubscribeRequestCallback = this._channelContext.onRealTimeSubscribeRequestCallback;
        }

        if (this._channelContext.onRealTimeSubscribeResponseCallback) {
          subscribeOptions.onSubscribeResponseCallback = this._channelContext.onRealTimeSubscribeResponseCallback;
        }

        if (FeatureEnablement.clientOfferDisabled || !peerConnection.supportsSetConfiguration || !peerConnection.supportsGetConfiguration) {
          peerConnection.close();
          peerConnection.dispose();

          if (SDK.forceGarbageCollectionOnRestart && !(FeatureEnablement.isMobile && SDK.skipGarbageCollectionOnMobileDevices)) {
            GarbageCollectorManager.forceGarbageCollection();
          }

          this._peerConnectionContext.peerConnection.value = null;

          return endPoint.subscribe(token, null, this._channelContext.failureCount.value, subscribeOptions);
        }

        return endPoint.subscribe(token, localOffer, this._channelContext.failureCount.value, subscribeOptions);
      })
      .then(response => {
        const {status} = response;

        this._channelContext.exists.value = this._channelContext.mapSubscribeStatusToExists(status);
        this._channelContext.state.value = this._channelContext.mapSubscribeStatusToChannelStatus(status);

        this._channelContext.applyStatus(status);

        if (status !== 'ok') {
          this._channelContext.stream.value = null;
          this._channelContext.lag.value = undefined ;

          this._channelContext.applySessionAndStreamPropertiesToVideoElement();

          this._logger.info(
            '[%s] Subscribe failed with status [%s]',
            this._channelContext.streamId,
            status
          );

          return;
        }

        const {stream, rtcConfiguration, setRemoteDescriptionResponse, createOfferDescriptionResponse, createAnswerDescriptionResponse, lag} = response;

        this._channelContext.stream.value = stream;
        this._channelContext.lag.value = lag;

        this._channelContext.applySessionAndStreamPropertiesToVideoElement();

        this._logger.debug(
          '[%s] Subscribe completed [%s] [%j] [%j] [%j] [%j]',
          this._channelContext.streamId,
          status,
          rtcConfiguration,
          setRemoteDescriptionResponse,
          createOfferDescriptionResponse,
          createAnswerDescriptionResponse
        );

        return this.applyRtcConfiguration(this._peerConnectionContext.peerConnection.value, rtcConfiguration as RTCConfiguration)
          .then(peerConnection => {
            let submitCandidatesTimeout: undefined | number;
            let cancelDiscovery = false;
            let discoveryCompleted = false;
            const candidates: RTCIceCandidate[] = [];

            if (!this._peerConnectionContext.peerConnection.value) {
              this._peerConnectionContext.peerConnection.value = peerConnection;
            }

            peerConnection.onicecandidate = (e): void => {
              if (this._channelContext.stream.value !== stream) {
                return;
              }

              if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                return;
              }

              if (cancelDiscovery) {
                return;
              }

              if (!SDK.sendLocalCandidates.value) {
                return;
              }

              if (e.candidate && e.candidate.candidate) {
                candidates.push(e.candidate);
              } else {
                discoveryCompleted = true;
              }

              if (!submitCandidatesTimeout) {
                submitCandidatesTimeout = GlobalScope.value.setTimeout(() => {
                  if (this._channelContext.stream.value !== stream) {
                    return;
                  }

                  if (cancelDiscovery) {
                    return;
                  }

                  const ignored = this._channelContext.endPoint.value?.addIceCandidates(stream, candidates, discoveryCompleted)
                    .then(({status, options}) => {
                      if (status !== 'ok') {
                        this._logger.warn('[%s] Failed to add ICE candidates with reason [%s]', this._channelContext.streamId, status);

                        return;
                      }

                      if (options?.includes('cancel')) {
                        cancelDiscovery = true;
                      }

                      this._logger.info('[%s] Added ICE candidates with reason [%s] and options [%s]', this._channelContext.streamId, status, options);
                    })
                    .catch(e => {
                      this._logger.error('[%s] Failed to add ICE candidates', this._channelContext.streamId, e);
                    });
                }, iceCandidateAccumulationInterval);

                this._disposables.add(new Disposable(() => GlobalScope.value.clearTimeout(submitCandidatesTimeout)));
              }
            };

            this._disposables.add(new Disposable(() => {
              peerConnection.onicecandidate = null;
              peerConnection.oniceconnectionstatechange = null;
            }));

            peerConnection.oniceconnectionstatechange = (): void => {
              if (this._channelContext.stream.value !== stream) {
                return;
              }

              if (this._peerConnectionContext.peerConnection.value !== peerConnection) {
                return;
              }

              const retryCallback = (): void => {
                const ignored = this._handleStreamFailure()
                  .catch(e => {
                    this._logger.error(
                      '[%s] Failed handling stream failure after peer connection stopped with state [%s]',
                      this._channelContext.streamId,
                      peerConnection.iceConnectionState,
                      e
                    );
                  });
              };

              switch (peerConnection.iceConnectionState) {
                case 'checking':
                case 'completed':
                case 'connected':
                case 'new':
                  return;

                case 'disconnected':
                case 'failed':
                  if (GlobalScope.value.navigatorIsOnLine) {
                    this._logger.info('[%s] ICE connection state changed to [%s], trying to reconnect', this._channelContext.streamId, peerConnection.iceConnectionState);
                    this.reconnectPeerConnection(this._channelContext.streamId, peerConnection, retryCallback);
                  }

                  return;
                case 'closed':
                  this._logger.info('[%s] ICE connection state changed to [%s], retrying to connect', this._channelContext.streamId, peerConnection.iceConnectionState);

                  retryCallback();

                  return;
                default:
                  assertUnreachable(peerConnection.iceConnectionState as never);
              }
            };

            const mediaStreamPromise = new Promise<MediaStream>((resolve, reject) => {
              if (!FeatureEnablement.onTrackDisabled) {
                const timeoutId = GlobalScope.value.setTimeout(() => reject(new Error('Stream setup timed out')), defaultStreamSetupTimeout);

                this._disposables.add(new Disposable(() => GlobalScope.value.clearTimeout(timeoutId)));

                peerConnection.ontrack = (e): void => {
                  GlobalScope.value.clearTimeout(timeoutId);
                  resolve(e.streams[0]);
                };

                return;
              }

              const trackListener = (e: RTCPeerConnectionEventMap['track' | 'addstream']): void => {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                GlobalScope.value.clearTimeout(timeoutId);
                peerConnection.removeEventListener('track', trackListener);
                peerConnection.removeEventListener('addstream', trackListener);

                if ('streams' in e) {
                  resolve(e.streams[0]);
                } else {
                  resolve(e.stream);
                }
              };

              const timeoutId = GlobalScope.value.setTimeout(() => {
                peerConnection.removeEventListener('track', trackListener);
                peerConnection.removeEventListener('addstream', trackListener);
                reject(new Error('Stream setup timed out'));
              }, defaultStreamSetupTimeout);

              peerConnection.addEventListener('track', trackListener);
              peerConnection.addEventListener('addstream', trackListener);
              this._disposables.add(new Disposable(() => GlobalScope.value.clearTimeout(timeoutId)));

              return;
            });

            return new Promise<void>(resolve => {
              resolve();
            }).then(() => {
              if (!setRemoteDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set local SDP offer [%s]', this._channelContext.streamId, setRemoteDescriptionResponse.sessionDescription.sdp);

              return peerConnection.setLocalDescription(setRemoteDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set local description [%j] with message [%s]', this._channelContext.streamId, setRemoteDescriptionResponse.sessionDescription, e.message);

                  throw e;
                });
            }).then(() => {
              if (!createAnswerDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set remote SDP answer [%s]', this._channelContext.streamId, createAnswerDescriptionResponse.sessionDescription.sdp);

              return peerConnection.setRemoteDescription(createAnswerDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set remote description [%j] with message [%s]', this._channelContext.streamId, createAnswerDescriptionResponse.sessionDescription, e.message);

                  throw e;
                });
            }).then(() => {
              if (!createOfferDescriptionResponse) {
                return;
              }

              this._logger.info('[%s] Set remote SDP offer [%s]', this._channelContext.streamId, createOfferDescriptionResponse.sessionDescription.sdp);

              return peerConnection.setRemoteDescription(createOfferDescriptionResponse.sessionDescription)
                .catch(e => {
                  this._logger.info('[%s] Failed to set remote description [%j] with message [%s]', this._channelContext.streamId, createOfferDescriptionResponse.sessionDescription, e.message);

                  throw e;
                })
                .then(() => {
                  return peerConnection.createAnswer({
                    offerToReceiveAudio: true,
                    offerToReceiveVideo: true
                  });
                }).then(answer => {
                  this._logger.info('[%s] Set local SDP answer [%j]', this._channelContext.streamId, answer);

                  const endPoint = this._channelContext.endPoint.value as EndPoint;

                  return endPoint.setRemoteDescription(stream, answer);
                }).then(({status, sessionDescription}) => {
                  this._channelContext.state.value = this.mapSetRemoteDescriptionStatusToChannelStatus(status);

                  if (status !== 'ok') {
                    this._channelContext.playing.value = false;
                    this._channelContext.standby.value = true;
                    this._channelContext.stopped.value = false;

                    return;
                  }

                  return peerConnection.setLocalDescription(sessionDescription as RTCSessionDescription)
                    .catch(e => {
                      this._logger.info('[%s] Failed to set local description [%j] with message [%s]', this._channelContext.streamId, sessionDescription, e.message);

                      throw e;
                    });
                });
            }).then(() => {
              listenOnStreamSetup.success(this._channelContext.streamId);

              return mediaStreamPromise;
            }).then(mediaStream => {
              if (requiresEncodedInsertableStreams && peerConnection?.currentRemoteDescription?.sdp) {
                const parsedRemoteDescription = new SdpParser(peerConnection.currentRemoteDescription.sdp);

                this._streamTransformContext.videoTrack.codec = VideoCodecMapping.convertVideoCodecTypeToVideoCodec(parsedRemoteDescription.videoCodec as VideoCodecType);
                this._streamTransformContext.audioTrack.codec = AudioCodecMapping.convertAudioCodecTypeToAudioCodec(parsedRemoteDescription.audioCodec as AudioCodecType);

                const receivers = peerConnection.getReceivers();
                // Note: This assumes that there is only one video MediaStreamTrack and one audio MediaStreamTrack
                const videoTrack = this._streamTransformContext.videoTrack.mediaStreamTrack = mediaStream.getVideoTracks()[0];
                // Both are expected to be present, but the type system makes them nullable, so we have to assert the presence
                const videoTrackReceiver = this._streamTransformContext.videoTrack.receiver = receivers.filter(receiver => receiver.track === videoTrack)[0];

                this._streamTransformContext.videoTrack.mid = peerConnection.supportsGetTransceivers
                  ? peerConnection.getTransceivers().filter(transceiver => transceiver.receiver === videoTrackReceiver)[0]?.mid
                  : null;

                const audioTrack = this._streamTransformContext.audioTrack.mediaStreamTrack = mediaStream.getAudioTracks()[0];
                const audioTrackReceiver = this._streamTransformContext.audioTrack.receiver = receivers.filter(receiver => receiver.track === audioTrack)[0];

                this._streamTransformContext.audioTrack.mid = peerConnection.supportsGetTransceivers
                  ? peerConnection.getTransceivers().filter(transceiver => transceiver.receiver === audioTrackReceiver)[0]?.mid
                  : null;

                this._disposables.add(InsertableStreams.createEncodedStreamPipeline(this._streamTransformContext));

                const requiresFrameTimingInformation = this._frameTimingInformationContext.shouldExtractTimecode.value || this._frameTimingInformationContext.shouldExtractTimestamps.value;

                if (requiresFrameTimingInformation) {
                  const frameTimingInformationManagerContext: FrameTimingInformationManagerContext = {
                    streamId: this._channelContext.streamId,
                    codec: this._streamTransformContext.videoTrack.codec,
                    videoElement: this._channelContext.videoElement,
                    decodedFrameTimingInformationSubject: this._frameTimingInformationContext.decodedFrameTimingInformation,
                    renderedFrameTimingInformationSubject: this._frameTimingInformationContext.renderedFrameTimingInformation,
                    isEnabledSubject: this._frameTimingInformationContext.isEnabled,
                    receiver: videoTrackReceiver,
                    mid: this._streamTransformContext.videoTrack.mid!,
                    trackId: this._streamTransformContext.videoTrack.mediaStreamTrack.id!,
                    transformMessageRouter: this._streamTransformContext.videoTrack.transformMessageRouter!,
                    transformFunctionNames: this._streamTransformContext.videoTrack.transformFunctionNames
                  };

                  this._disposables.add(new FrameTimingInformationManager(frameTimingInformationManagerContext));
                }

                const requiresEncodedMediaTransform = this._streamTransformContext.hasEncodedInsertableStreams.value;

                if (requiresEncodedMediaTransform) {
                  const encodedMediaTransformManagerContext: EncodedMediaTransformManagerContext = {
                    videoTrack: this._streamTransformContext.videoTrack,
                    audioTrack: this._streamTransformContext.audioTrack,
                    encodedVideoStreamSink: this._streamTransformContext.encodedVideoStreamSink!,
                    encodedAudioStreamSink: this._streamTransformContext.encodedAudioStreamSink!
                  };

                  this._disposables.add(new EncodedMediaTransformManager(encodedMediaTransformManagerContext));
                }

                InsertableStreams.configureEncodedStreamPipeline(this._streamTransformContext);
              }

              if (this._streamTransformContext.hasInsertableStreams.value) {
                const {transformedStream, disposables} = InsertableStreams.configureInsertableStreamTransformation(mediaStream, this._streamTransformContext.videoStreamTransformCallback, this._streamTransformContext.audioStreamTransformCallback);

                this._disposables.add(disposables);
                mediaStream = transformedStream;
              }

              this._peerConnectionContext.mediaStream.value = mediaStream;

              const rtcConnectionMonitor = new RtcConnectionMonitor(
                peerConnection, mediaStream,
                this._channelContext.endPoint.value?.roundTripTime ? this._channelContext.endPoint.value.roundTripTime / 4 : 0,
                SDK.applicationActivityMonitor!.isForeground
              );

              this._disposables.add(rtcConnectionMonitor);

              const rtcConnectionMonitorStatisticSubscription = rtcConnectionMonitor.rtcStatistic.subscribe(statistics => {
                this._channelContext.rtcStatistics.value = statistics;

                if (!statistics) {
                  return;
                }

                if (!this._channelContext.rtcVideoStatistic && !this._channelContext.rtcAudioStatistic) {
                  this._channelContext.rtcAudioStatistic = statistics.audio;
                  this._channelContext.rtcVideoStatistic = statistics.video;

                  return;
                }

                if (!SDK.applicationActivityMonitor!.isForeground.value) {
                  this._channelContext.rtcAudioStatistic = statistics.audio;
                  this._channelContext.rtcVideoStatistic = statistics.video;

                  return;
                }

                let audioTrackFailed = false;
                let videoTrackFailed = false;

                if (statistics.audio) {
                  if (this._channelContext.rtcAudioStatistic && this._channelContext.rtcAudioStatistic.timestamp !== statistics.audio.timestamp) {
                    audioTrackFailed = this._channelContext.rtcAudioStatistic && this._channelContext.rtcAudioStatistic.bytesReceived === statistics.audio.bytesReceived;

                    if (audioTrackFailed && GlobalScope.value.navigatorIsOnLine) {
                      this._logger.info(
                        '[%s] Audio track failed with last bytesReceived [%s] is equal to previous bytesReceived [%s] within [%s]',
                        this._channelContext.streamId,
                        statistics.audio.bytesReceived,
                        this._channelContext.rtcAudioStatistic.bytesReceived,
                        new Durations(statistics.audio.timestamp - this._channelContext.rtcAudioStatistic.timestamp).toIsoString()
                      );
                    }

                    this._channelContext.rtcAudioStatistic = statistics.audio;
                  }
                }

                if (statistics.video) {
                  if (this._channelContext.rtcVideoStatistic && this._channelContext.rtcVideoStatistic.timestamp !== statistics.video.timestamp) {
                    videoTrackFailed = this._channelContext.rtcVideoStatistic && this._channelContext.rtcVideoStatistic.bytesReceived === statistics.video.bytesReceived;

                    if (videoTrackFailed && GlobalScope.value.navigatorIsOnLine) {
                      this._logger.info(
                        '[%s] Video track failed with last bytesReceived [%s] is equal to previous bytesReceived [%s] within [%s]',
                        this._channelContext.streamId,
                        statistics.video.bytesReceived,
                        this._channelContext.rtcVideoStatistic.bytesReceived,
                        new Durations(statistics.video.timestamp - this._channelContext.rtcVideoStatistic.timestamp).toIsoString()
                      );
                    }

                    this._channelContext.rtcVideoStatistic = statistics.video;
                  }
                }

                if (!GlobalScope.value.navigatorIsOnLine) {
                  this._channelContext.state.value = ChannelState.Offline;

                  return;
                }

                if (videoTrackFailed || audioTrackFailed) {
                  const retryCallback = (): void => {
                    rtcConnectionMonitor.dispose();

                    const ignored = this._handleStreamFailure()
                      .catch(e => {
                        this._logger.error(
                          '[%s] Failed handling stream failure after track stopped with state [%s]',
                          this._channelContext.streamId,
                          peerConnection.iceConnectionState,
                          e
                        );
                      });
                  };

                  this.reconnectPeerConnection(this._channelContext.streamId, peerConnection, retryCallback);
                } else {
                  this._peerConnectionContext.peerConnectionReconnectAttempts = 0;

                  if (this._channelContext.state.value !== ChannelState.Reconnecting && this._channelContext.state.value !== ChannelState.Offline) {
                    return;
                  }

                  this._channelContext.state.value = this._channelContext.playing.value ? ChannelState.Playing : ChannelState.Paused;
                  this._logger.info('Channel state restored to [%s] after reconnecting', ChannelState[this._channelContext.state.value]);
                }
              });

              this._disposables.add(rtcConnectionMonitorStatisticSubscription);

              if (!SDK.automaticallyPlayMediaStream) {
                this._channelContext.autoMuted.value = false;
                this._channelContext.autoPaused.value = true;
                this._channelContext.loading.value = false;
                this._channelContext.playing.value = false;
                this._channelContext.state.value = ChannelState.Paused;

                return;
              }

              return playMediaStreamInVideoElement(mediaStream);
            });
          });
      });
  }

  private async applyRtcConfiguration(
    optionalPeerConnection: IPeerConnection | null,
    rtcConfiguration: RTCConfiguration): Promise<IPeerConnection> {
    if (!optionalPeerConnection) {
      rtcConfiguration = RtcConfigurationManager.truncateIceServers(rtcConfiguration);

      return SDK.peerConnectionFactory.value.createPeerConnection(rtcConfiguration);
    }

    const newRtcConfiguration = {
      ...optionalPeerConnection.getConfiguration(),
      ...rtcConfiguration
    };

    optionalPeerConnection.setConfiguration(newRtcConfiguration);

    return optionalPeerConnection;
  }

  private reconnectPeerConnection(streamId: string, peerConnection: IPeerConnection, retryCallback: () => void): void {
    if (!SDK.applicationActivityMonitor!.isForeground.value) {
      this._logger.debug('[%s] Skipping peerConnection reconnection attempt while not in foreground (peerConnection state [%s])', streamId, peerConnection.iceConnectionState);

      return;
    }

    if (peerConnection.iceConnectionState === 'closed') {
      return;
    }

    switch (this._channelContext.state.value) {
      case ChannelState.Paused:
      case ChannelState.Stopped:
      case ChannelState.Unauthorized:
      case ChannelState.GeoRestricted:
      case ChannelState.GeoBlocked:
      case ChannelState.UnsupportedFeature:
      case ChannelState.ConfigurationError:
        this._logger.debug('[%s] Skipping peerConnection reconnection in deliberately-terminal state [%s]', streamId, ChannelState[this._channelContext.state.value]);

        return;
      default:
        break;
    }

    this._channelContext.state.value = ChannelState.Reconnecting;

    if (this._peerConnectionContext.peerConnectionReconnectAttempts < SDK.maximalNumberOfPeerConnectionReconnectAttempts || !SDK.automaticallyReconnectPeerConnection) {
      this._peerConnectionContext.peerConnectionReconnectAttempts++;

      const isClientOfferFlow = peerConnection.currentLocalDescription?.type === 'offer';

      if (FeatureEnablement.clientOfferDisabled ||
        !peerConnection.supportsSetConfiguration ||
        !peerConnection.supportsGetConfiguration ||
        !isClientOfferFlow
      ) {
        return;
      }

      this._logger.info('Reconnecting peer connection by restarting ICE');

      const currentLocalDescription = peerConnection.currentLocalDescription;
      const ignored = peerConnection.createOffer({iceRestart: true}).then(offer => {
        return peerConnection.setLocalDescription(offer).then(() => {
          return peerConnection.setLocalDescription(currentLocalDescription);
        });
      })
        .catch(e => {
          this._logger.error('Failed to reconnect peer connection', e);
          this._peerConnectionContext.peerConnectionReconnectAttempts = 0;
          retryCallback();
        });

      return;
    }

    this._logger.info('Failed to reconnect peer connection after [%s] attempts, performing full recovery', this._peerConnectionContext.peerConnectionReconnectAttempts);
    this._peerConnectionContext.peerConnectionReconnectAttempts = 0;
    retryCallback();
  }

  isStreamHealthy(): Promise<boolean> {
    const videoElement = this._channelContext.videoElement.value;

    if (!videoElement) {
      return Promise.resolve(false);
    }

    const mediaStream = this._peerConnectionContext.mediaStream.value;
    const hasActiveTracks = mediaStream?.getTracks().some(track => track.readyState === 'live') ?? false;

    if (videoElement.paused || !hasActiveTracks) {
      return Promise.resolve(false);
    }

    return VideoFrameDetector.isRenderingFrames(videoElement);
  }

  dispose(): void {
    this._disposables.dispose();
  }

  private mapSetRemoteDescriptionStatusToChannelStatus(status: SetRemoteDescriptionStatus): ChannelState {
    switch (status) {
      case 'ok':
        return ChannelState.Starting;
      case 'unauthorized':
        return ChannelState.Unauthorized;
      case 'not-found':
      case 'capacity':
      case 'rate-limited':
      case 'timeout':
        return ChannelState.Recovering;
      case 'failed':
        return ChannelState.Error;
      default:
        assertUnreachable(status);
    }
  }
}